import React, { useEffect, useState } from "react";
import { Button } from "src/components/ui/button";
import { Label } from "src/components/ui/label";
import { Textarea } from "src/components/ui/textarea";
import { getKnowledgeFileName } from "src/utils/brandTheme";

// eslint-disable-next-line custom/no-hardcoded-brand-name -- localStorage key
const STORAGE_KEY_PREFIX = "tierzero_onboarding_company_context_";

// Helper to get storage key for an organization
function getStorageKey(organizationId: string): string {
  return `${STORAGE_KEY_PREFIX}${organizationId}`;
}

// Helper to save company context to localStorage
export function saveCompanyContextToStorage(
  organizationId: string,
  data: { products: string; techStack: string }
) {
  try {
    localStorage.setItem(getStorageKey(organizationId), JSON.stringify(data));
  } catch {
    // Ignore localStorage errors
  }
}

// Helper to load company context from localStorage
export function loadCompanyContextFromStorage(
  organizationId: string
): { products: string; techStack: string } | null {
  try {
    const stored = localStorage.getItem(getStorageKey(organizationId));
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore localStorage errors
  }
  return null;
}

// Helper to clear company context from localStorage (call after onboarding completes)
export function clearCompanyContextFromStorage(organizationId: string) {
  try {
    localStorage.removeItem(getStorageKey(organizationId));
  } catch {
    // Ignore localStorage errors
  }
}

interface CompanyContextStepProps {
  organizationId: string;
  brandTheme?: string | null;
  onContinue: (data: { products: string; techStack: string }) => void;
  isLoading?: boolean;
  isInitializationStarted?: boolean;
  productName: string;
  onCancel?: () => void;
}

export const CompanyContextStep: React.FC<CompanyContextStepProps> = ({
  organizationId,
  brandTheme,
  onContinue,
  isLoading,
  isInitializationStarted = false,
  onCancel,
  productName,
}) => {
  // Initialize from localStorage if available
  const [products, setProducts] = useState(() => {
    const stored = loadCompanyContextFromStorage(organizationId);
    return stored?.products || "";
  });
  const [techStack, setTechStack] = useState(() => {
    const stored = loadCompanyContextFromStorage(organizationId);
    return stored?.techStack || "";
  });

  // Save to localStorage when values change (only if not already initialized)
  useEffect(() => {
    if (!isInitializationStarted) {
      saveCompanyContextToStorage(organizationId, { products, techStack });
    }
  }, [organizationId, products, techStack, isInitializationStarted]);

  const handleSubmit = () => {
    onContinue({ products, techStack });
  };

  const isValid = products.trim().length > 0 && techStack.trim().length > 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-foreground">Help {productName} Understand Your Company</h2>
        <p className="text-muted-foreground mt-2">
          Help the agent understand what your business does and how your systems work.
          This context helps it initialize its {getKnowledgeFileName(brandTheme)} where to look and what matters when investigating issues.
        </p>
      </div>

      {isInitializationStarted && (
        <div className="bg-primary/10 border border-primary/30 rounded-lg px-4 py-3">
          <p className="text-primary text-sm">
            {productName} is already learning from this context. These fields cannot be edited while initialization is in progress.
          </p>
        </div>
      )}

      <div className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="products">
            What does your company do?
          </Label>
          <p className="text-muted-foreground text-sm">
            In one sentence, describe your product and what your customers use it for. This helps the agent understand what matters most when things go wrong.
          </p>
          <Textarea
            id="products"
            value={products}
            onChange={(e) => setProducts(e.target.value)}
            placeholder="We are Social Corp, a social media company where you can connect with your friends online, send messages, and post photos."
            rows={4}
            disabled={isInitializationStarted}
            className={isInitializationStarted ? "opacity-60 cursor-not-allowed" : ""}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="techStack">
            What's your tech stack?
          </Label>
          <p className="text-muted-foreground text-sm">
            List 3-5 bullets covering your key technologies, infrastructure, known trouble spots, and observability tools. The more specific you are, the better the agent can help.
          </p>
          <Textarea
            id="techStack"
            value={techStack}
            onChange={(e) => setTechStack(e.target.value)}
            placeholder={`- We run FastAPI-based Python microservices, use postgres as our database layer, and run on k8s clusters in AWS. 
- Most of our infrastructure problems lay in our database layer in our monolith 
- We use Y Corp for CI/CD in our modern microservices, but Z Corp in some of our legacy monolith in the legacy-photos repo.
- We use A Corp for observability in our modern microservices, but B Corp in our legacy monolith in the legacy-photos repo.`}
            rows={4}
            disabled={isInitializationStarted}
            className={isInitializationStarted ? "opacity-60 cursor-not-allowed" : ""}
          />
        </div>
      </div>

      <div className="flex justify-between pt-4">
        {onCancel ? (
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : <div />}
        <Button onClick={handleSubmit} disabled={(!isValid && !isInitializationStarted) || isLoading}>
          {isLoading ? "Saving..." : "Continue"}
        </Button>
      </div>
    </div>
  );
};
